import { useState, useCallback } from "react";

export interface JobLogEntry {
  id: string;
  timestamp: number;
  calcType: string;
  label: string;
  inputs: Record<string, string>;
  results: { label: string; value: string; unit: string }[];
  note: string;
}

const STORAGE_KEY = "trades-toolbelt-job-log";

const readEntries = (): JobLogEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeEntries = (entries: JobLogEntry[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
};

export const useJobLog = () => {
  const [entries, setEntries] = useState<JobLogEntry[]>(readEntries);

  const refresh = useCallback(() => setEntries(readEntries()), []);

  const addEntry = useCallback((entry: Omit<JobLogEntry, "id" | "timestamp">) => {
    const newEntry: JobLogEntry = {
      ...entry,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
    };
    const updated = [newEntry, ...readEntries()];
    writeEntries(updated);
    setEntries(updated);
    return newEntry;
  }, []);

  const deleteEntry = useCallback((id: string) => {
    const updated = readEntries().filter((e) => e.id !== id);
    writeEntries(updated);
    setEntries(updated);
  }, []);

  const clearAll = useCallback(() => {
    writeEntries([]);
    setEntries([]);
  }, []);

  return { entries, addEntry, deleteEntry, clearAll, refresh };
};
